import React, { useEffect, useMemo, useState, useRef } from 'react';
import { X, Monitor, Smartphone, Square, Grid3X3, Settings, Check, Plus, Trash2, MapPin, ChevronDown, Search } from 'lucide-react';
import { listProfiles, createProfile, deleteProfile } from '../api/profileApi';

/**
 * ProfilePickerModal
 *
 * One modal, three use sites:
 *   1. First-run setup (device has profileId === null, picker is the welcome)
 *   2. TVDetails "Change profile" action
 *   3. Hardware-change card's "Pick a new profile" action
 *
 * Contract:
 *   <ProfilePickerModal
 *     open
 *     device={tv}                        // required — for header + fit-score
 *     reason="first-run" | "change" | "hardware-change"
 *     onClose={() => ...}
 *     onApply={(profileId, profile) => Promise}   // caller wires apply + refresh
 *   />
 *
 * Design rules it enforces (see CLAUDE.md, Schedule Tier-3 redesign lessons):
 *   - Sections, not fields.
 *   - Show don't ask — live preview of how content will look on each profile
 *     card, fit-score badge if EDID reports something, etc.
 *   - Sensible defaults — first card auto-highlighted based on best match.
 *   - One click per row target (card click → select, no nested controls).
 */

// lucide icon map keyed by profile.icon field. Keep in sync with the
// strings the backend's SYSTEM_PROFILES seed writes. Unknown icons fall
// back to Monitor.
const ICON_MAP = {
  monitor: Monitor,
  smartphone: Smartphone,
  square: Square,
  grid: Grid3X3,
  'grid-3x3': Grid3X3,
  settings: Settings,
};
const IconFor = ({ name, size = 28 }) => {
  const Comp = ICON_MAP[name] || Monitor;
  return <Comp size={size} />;
};

/**
 * Score a profile against a device's EDID fingerprint. Higher = better fit.
 * Used to sort cards so the most likely option is front-and-center.
 *
 *   - Exact match on hdmi_expected (either orientation) → 100
 *   - Exact match on content dims (either orientation)   → 90
 *   - Same orientation (both landscape or both portrait) → 40
 *   - Otherwise                                          → 0
 */
function scoreProfileFit(profile, device) {
  const fp = device?.edidFingerprint;
  if (!fp?.width || !fp?.height) return 0;
  const ew = fp.width;
  const eh = fp.height;

  const hw = profile.hdmiExpected?.width || profile.content?.width;
  const hh = profile.hdmiExpected?.height || profile.content?.height;
  if (hw && hh) {
    if ((ew === hw && eh === hh) || (ew === hh && eh === hw)) return 100;
  }
  const cw = profile.content?.width;
  const ch = profile.content?.height;
  if (cw && ch) {
    if ((ew === cw && eh === ch) || (ew === ch && eh === cw)) return 90;
  }
  const devLandscape = ew >= eh;
  const profLandscape = (cw || 0) >= (ch || 0);
  if (devLandscape === profLandscape) return 40;
  return 0;
}

/**
 * Tiny inline preview: a scaled mock of the screen, content aspect ratio
 * drawn inside. Shows at-a-glance whether the screen is landscape/portrait
 * and what ratio content should be authored at.
 */
function ProfilePreview({ profile, selected }) {
  const cw = profile.content?.width || 1920;
  const ch = profile.content?.height || 1080;
  const maxDim = 80;
  const scale = maxDim / Math.max(cw, ch);
  const w = Math.round(cw * scale);
  const h = Math.round(ch * scale);
  return (
    <div style={{
      width: maxDim,
      height: maxDim,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}>
      <div style={{
        width: w,
        height: h,
        borderRadius: 4,
        background: selected ? 'linear-gradient(135deg, #fef3c7, #fde68a)' : '#f1f5f9',
        border: `2px solid ${selected ? '#c8a147' : '#cbd5e1'}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 9,
        fontWeight: 600,
        color: selected ? '#92400e' : '#64748b',
      }}>
        {profile.content?.aspectRatio || `${cw}×${ch}`}
      </div>
    </div>
  );
}

const ProfilePickerModal = ({ open, device, reason = 'change', onClose, onApply, locations = [] }) => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState(null);
  const [showCustomForm, setShowCustomForm] = useState(false);

  // Device identity fields — shown in first-run so the admin names and
  // locates the device in one step instead of profiling then hunting for
  // the rename button later.
  const [deviceName, setDeviceName] = useState('');
  const [deviceLocation, setDeviceLocation] = useState('');
  // Manage view — lets admins delete custom profiles. Keeps the
  // picker as the single entry point for all profile operations
  // (pick + create + delete) so admins don't have to learn a
  // second management screen.
  const [showManage, setShowManage] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [customDraft, setCustomDraft] = useState({
    name: '',
    physicalType: 'custom',
    contentWidth: '',
    contentHeight: '',
    hdmiW: '',
    hdmiH: '',
  });

  // Fetch profiles on open. Always re-fetches when the modal is re-opened
  // so a profile created on another session shows up immediately.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setApplyError(null);
    setSelectedId(null);
    setShowCustomForm(false);
    setDeviceName(device?.displayName || '');
    // Only pre-fill location if it's a known site, not auto-seeded garbage
    const existingLoc = device?.location || '';
    setDeviceLocation(locations.includes(existingLoc) ? existingLoc : '');
    listProfiles()
      .then((data) => {
        if (cancelled) return;
        setProfiles(data.profiles || []);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err?.message || 'Failed to load profiles');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open]);

  // Sort by fit score (best match first) — derived, not stored, so it
  // re-ranks automatically when the EDID changes via a new heartbeat.
  const ranked = useMemo(() => {
    if (!profiles.length) return [];
    const withScore = profiles.map((p) => ({ p, score: scoreProfileFit(p, device) }));
    withScore.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      // Tie-break: system profiles before user profiles, then by name
      if (a.p.isSystem !== b.p.isSystem) return a.p.isSystem ? -1 : 1;
      return (a.p.name || '').localeCompare(b.p.name || '');
    });
    return withScore;
  }, [profiles, device]);

  // Auto-select the top card when the list loads, unless the device already
  // has a profile (in which case select that one so "save" is a no-op).
  useEffect(() => {
    if (!profiles.length) return;
    if (device?.profileId && profiles.find((p) => p.profileId === device.profileId)) {
      setSelectedId(device.profileId);
      return;
    }
    setSelectedId(ranked[0]?.p?.profileId || null);
  }, [profiles, device?.profileId, ranked]);

  const handleApply = async () => {
    if (!selectedId) return;
    const profile = profiles.find((p) => p.profileId === selectedId);
    if (!profile) return;
    setApplying(true);
    setApplyError(null);
    try {
      const meta = {
        displayName: deviceName.trim() || undefined,
        location: deviceLocation.trim() || undefined,
      };
      await onApply?.(profile.profileId, profile, meta);
      onClose?.();
    } catch (err) {
      setApplyError(err?.message || 'Failed to apply profile');
    } finally {
      setApplying(false);
    }
  };

  const handleDeleteProfile = async (profileId) => {
    setDeletingId(profileId);
    setApplyError(null);
    try {
      await deleteProfile(profileId);
      setProfiles((prev) => prev.filter((p) => p.profileId !== profileId));
    } catch (err) {
      const inUse = err?.inUseCount;
      if (typeof inUse === 'number' && inUse > 0) {
        setApplyError(`"${profiles.find((p) => p.profileId === profileId)?.name}" is in use on ${inUse} screen${inUse === 1 ? '' : 's'}. Reassign those screens first.`);
      } else {
        setApplyError(err?.message || 'Failed to delete profile');
      }
    } finally {
      setDeletingId(null);
    }
  };

  const handleCreateCustom = async () => {
    const name = customDraft.name.trim();
    const cw = Number(customDraft.contentWidth);
    const ch = Number(customDraft.contentHeight);
    if (!name || !cw || !ch) {
      setApplyError('Name, content width, and height are required');
      return;
    }
    setApplying(true);
    setApplyError(null);
    try {
      const payload = {
        name,
        physicalType: customDraft.physicalType,
        icon:
          customDraft.physicalType === 'portrait_kiosk' ? 'smartphone'
          : customDraft.physicalType === 'led_wall' ? 'grid'
          : customDraft.physicalType === 'square' ? 'square'
          : 'monitor',
        content: { width: cw, height: ch },
      };
      if (Number(customDraft.hdmiW) && Number(customDraft.hdmiH)) {
        payload.hdmiExpected = { width: Number(customDraft.hdmiW), height: Number(customDraft.hdmiH) };
      }
      const created = await createProfile(payload);
      await onApply?.(created.profileId, created);
      onClose?.();
    } catch (err) {
      setApplyError(err?.message || 'Failed to create profile');
    } finally {
      setApplying(false);
    }
  };

  if (!open) return null;

  const header = reason === 'hardware-change'
    ? 'Re-confirm screen setup'
    : 'Screen setup';

  const subheader = reason === 'hardware-change'
    ? 'The HDMI hardware has changed. Confirm the details below match the new display.'
    : 'Name, locate, and configure this screen.';

  const edid = device?.edidFingerprint;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="profile-picker-title"
      style={styles.backdrop}
      onClick={onClose}
    >
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* ── Header ── */}
        <div style={styles.header}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div id="profile-picker-title" style={styles.title}>{header}</div>
            <div style={styles.subtitle}>{subheader}</div>
            {edid && (
              <div style={styles.edidLine}>
                HDMI reports <strong>{edid.width}×{edid.height}</strong>
                {edid.manufacturer ? ` · ${edid.manufacturer}` : ''}
                {edid.monitorName ? ` · "${edid.monitorName}"` : ''}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={styles.closeBtn}
          >
            <X size={18} />
          </button>
        </div>

        {/* ── Body ── */}
        <div style={styles.body}>
          {/* Device identity fields — always shown */}
          <div style={styles.identitySection}>
              <div style={styles.identityField}>
                <label style={styles.identityLabel}>Screen name</label>
                <input
                  type="text"
                  placeholder="e.g. Lobby TV, Floor 3 Reception"
                  value={deviceName}
                  onChange={(e) => setDeviceName(e.target.value)}
                  maxLength={80}
                  style={styles.identityInput}
                />
              </div>
              <div style={styles.identityField}>
                <label style={styles.identityLabel}>Location</label>
                <LocationPicker
                  value={deviceLocation}
                  onChange={setDeviceLocation}
                  locations={locations}
                />
              </div>
            </div>

          {loading && <div style={styles.loading}>Loading profiles…</div>}
          {loadError && <div style={styles.error}>{loadError}</div>}

          {!loading && !loadError && !showCustomForm && (
            <div style={styles.grid}>
              {ranked.map(({ p, score }) => {
                const selected = p.profileId === selectedId;
                return (
                  <button
                    key={p.profileId}
                    type="button"
                    onClick={() => setSelectedId(p.profileId)}
                    style={{
                      ...styles.card,
                      ...(selected ? styles.cardSelected : {}),
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%' }}>
                      <ProfilePreview profile={p} selected={selected} />
                      <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <IconFor name={p.icon} size={18} />
                          <span style={styles.cardName}>{p.name}</span>
                          {score >= 100 && <span style={styles.matchBadge}>✓ matches EDID</span>}
                          {score === 90 && <span style={styles.matchBadge}>✓ matches</span>}
                          {!p.isSystem && <span style={styles.userBadge}>custom</span>}
                        </div>
                        <div style={styles.cardMeta}>
                          {p.content.width}×{p.content.height} · {p.content.aspectRatio}
                          {p.hdmiExpected?.width &&
                            (p.hdmiExpected.width !== p.content.width ||
                              p.hdmiExpected.height !== p.content.height) && (
                              <span style={{ color: '#94a3b8' }}>
                                {' '}· HDMI {p.hdmiExpected.width}×{p.hdmiExpected.height}
                              </span>
                            )}
                        </div>
                        {p.description && (
                          <div style={styles.cardDesc}>{p.description}</div>
                        )}
                      </div>
                      {selected && (
                        <div style={styles.selectedCheck}>
                          <Check size={16} />
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}

              <button
                type="button"
                onClick={() => { setShowCustomForm(true); setApplyError(null); }}
                style={{ ...styles.card, ...styles.addCard }}
              >
                <Plus size={20} />
                <span style={{ fontWeight: 600, color: '#64748b' }}>Create custom profile</span>
              </button>
            </div>
          )}

          {!loading && !loadError && showManage && (
            <div style={styles.form}>
              <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
                Delete custom profiles you no longer need. Built-in profiles cannot be removed.
                Profiles in use by any device are blocked from deletion — reassign those devices first.
              </div>
              {profiles.filter((p) => !p.isSystem).length === 0 && (
                <div style={{
                  padding: '20px',
                  textAlign: 'center',
                  color: '#94a3b8',
                  fontSize: 13,
                  background: '#f8fafc',
                  borderRadius: 8,
                  border: '1px dashed #e2e8f0',
                }}>
                  No custom profiles yet. Create one from the picker to make reusable variants.
                </div>
              )}
              {profiles.filter((p) => !p.isSystem).map((p) => (
                <div
                  key={p.profileId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 14px',
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    background: '#fff',
                  }}
                >
                  <IconFor name={p.icon} size={18} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{p.name}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>
                      {p.content.width}×{p.content.height} · {p.content.aspectRatio}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDeleteProfile(p.profileId)}
                    disabled={deletingId === p.profileId}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '6px 10px',
                      fontSize: 11,
                      fontWeight: 600,
                      color: '#b91c1c',
                      background: '#fef2f2',
                      border: '1px solid #fecaca',
                      borderRadius: 6,
                      cursor: deletingId === p.profileId ? 'wait' : 'pointer',
                    }}
                  >
                    <Trash2 size={12} />
                    {deletingId === p.profileId ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setShowManage(false)}
                style={styles.linkBtn}
              >
                ← Back to profile list
              </button>
            </div>
          )}

          {!loading && !loadError && showCustomForm && (
            <div style={styles.form}>
              <div style={styles.formRow}>
                <label style={styles.label}>Name</label>
                <input
                  type="text"
                  value={customDraft.name}
                  onChange={(e) => setCustomDraft((d) => ({ ...d, name: e.target.value }))}
                  placeholder="e.g. Hyderabad Lobby LED"
                  maxLength={80}
                  style={styles.input}
                />
              </div>
              <div style={styles.formRow}>
                <label style={styles.label}>Physical type</label>
                <select
                  value={customDraft.physicalType}
                  onChange={(e) => setCustomDraft((d) => ({ ...d, physicalType: e.target.value }))}
                  style={styles.input}
                >
                  <option value="landscape_tv">Landscape TV</option>
                  <option value="portrait_kiosk">Portrait kiosk</option>
                  <option value="led_wall">LED wall</option>
                  <option value="square">Square</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              <div style={styles.formRow}>
                <label style={styles.label}>Content dimensions (px)</label>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input
                    type="number"
                    value={customDraft.contentWidth}
                    onChange={(e) => setCustomDraft((d) => ({ ...d, contentWidth: e.target.value }))}
                    placeholder="width"
                    style={{ ...styles.input, width: 110 }}
                  />
                  <span style={{ color: '#94a3b8' }}>×</span>
                  <input
                    type="number"
                    value={customDraft.contentHeight}
                    onChange={(e) => setCustomDraft((d) => ({ ...d, contentHeight: e.target.value }))}
                    placeholder="height"
                    style={{ ...styles.input, width: 110 }}
                  />
                </div>
              </div>
              <div style={styles.formRow}>
                <label style={styles.label}>
                  HDMI expected (px) <span style={{ fontWeight: 400, color: '#94a3b8' }}>optional</span>
                </label>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input
                    type="number"
                    value={customDraft.hdmiW}
                    onChange={(e) => setCustomDraft((d) => ({ ...d, hdmiW: e.target.value }))}
                    placeholder="width"
                    style={{ ...styles.input, width: 110 }}
                  />
                  <span style={{ color: '#94a3b8' }}>×</span>
                  <input
                    type="number"
                    value={customDraft.hdmiH}
                    onChange={(e) => setCustomDraft((d) => ({ ...d, hdmiH: e.target.value }))}
                    placeholder="height"
                    style={{ ...styles.input, width: 110 }}
                  />
                </div>
                <div style={styles.helpText}>
                  Only fill in if the HDMI cable advertises different dimensions than the panel (e.g. a
                  vertical kiosk with a downstream scaler that reports 1920×1080).
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowCustomForm(false)}
                style={styles.linkBtn}
              >
                ← Back to profile list
              </button>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div style={styles.footer}>
          {applyError && <div style={{ ...styles.error, flex: 1, marginRight: 12 }}>{applyError}</div>}
          {!showCustomForm && !showManage && !applyError && (
            <button
              type="button"
              onClick={() => { setShowManage(true); setApplyError(null); }}
              style={{
                ...styles.linkBtn,
                flex: 1,
                textAlign: 'left',
                paddingLeft: 4,
              }}
            >
              Manage custom profiles
            </button>
          )}
          <button type="button" onClick={onClose} style={styles.cancelBtn}>Cancel</button>
          {showManage ? null : showCustomForm ? (
            <button
              type="button"
              onClick={handleCreateCustom}
              disabled={applying}
              style={{ ...styles.primaryBtn, opacity: applying ? 0.6 : 1 }}
            >
              {applying ? 'Creating…' : 'Create & apply'}
            </button>
          ) : (
            <SaveButton
              selectedId={selectedId}
              applying={applying}
              deviceName={deviceName}
              deviceLocation={deviceLocation}
              onApply={handleApply}
            />
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Custom location picker — styled dropdown with search + "Add new" inline.
 */
const LocationPicker = ({ value, onChange, locations = [] }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = locations.filter(
    (loc) => !search || loc.toLowerCase().includes(search.toLowerCase())
  );

  const handleAddConfirm = () => {
    const trimmed = draft.trim();
    if (trimmed) {
      onChange(trimmed);
      setAdding(false);
      setDraft('');
      setOpen(false);
    }
  };

  const lp = locPickerStyles;

  // No locations → just a text input
  if (!locations.length) {
    return (
      <input
        type="text"
        placeholder="e.g. Building A, Tower 2 Entrance"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={120}
        style={styles.identityInput}
      />
    );
  }

  return (
    <div ref={ref} style={lp.wrapper}>
      <button
        type="button"
        onClick={() => { setOpen(!open); setSearch(''); setAdding(false); }}
        style={{ ...lp.trigger, ...(open ? lp.triggerOpen : {}) }}
      >
        <MapPin size={14} style={{ color: '#94a3b8', flexShrink: 0 }} />
        <span style={{
          flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          color: value ? '#0f172a' : '#94a3b8',
        }}>
          {value || 'Select a location…'}
        </span>
        <ChevronDown size={14} style={{
          color: '#94a3b8', flexShrink: 0,
          transform: open ? 'rotate(180deg)' : 'none',
          transition: 'transform 0.15s',
        }} />
      </button>

      {open && (
        <div style={lp.dropdown}>
          {/* Search within locations */}
          {locations.length > 5 && (
            <div style={lp.searchRow}>
              <Search size={13} style={{ color: '#94a3b8', flexShrink: 0 }} />
              <input
                type="text"
                placeholder="Search locations…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
                style={lp.searchInput}
              />
            </div>
          )}

          <div style={lp.list}>
            {filtered.map((loc) => (
              <button
                key={loc}
                type="button"
                onClick={() => { onChange(loc); setOpen(false); }}
                style={{ ...lp.item, ...(loc === value ? lp.itemSelected : {}) }}
              >
                <MapPin size={12} style={{ color: loc === value ? '#c8a147' : '#94a3b8', flexShrink: 0 }} />
                <span style={{ flex: 1, textAlign: 'left' }}>{loc}</span>
                {loc === value && <Check size={14} style={{ color: '#c8a147', flexShrink: 0 }} />}
              </button>
            ))}
            {filtered.length === 0 && search && (
              <div style={lp.empty}>No locations match "{search}"</div>
            )}
          </div>

          {/* Add new location */}
          <div style={lp.addSection}>
            {adding ? (
              <div style={lp.addRow}>
                <input
                  type="text"
                  placeholder="New location name…"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAddConfirm(); }}
                  autoFocus
                  maxLength={120}
                  style={lp.addInput}
                />
                <button
                  type="button"
                  onClick={handleAddConfirm}
                  disabled={!draft.trim()}
                  style={{ ...lp.addConfirmBtn, opacity: draft.trim() ? 1 : 0.4 }}
                >
                  <Check size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => { setAdding(false); setDraft(''); }}
                  style={lp.addCancelBtn}
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setAdding(true)}
                style={lp.addBtn}
              >
                <Plus size={13} />
                <span>Add new location</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const locPickerStyles = {
  wrapper: { position: 'relative' },
  trigger: {
    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
    padding: '8px 12px', fontSize: 14, fontFamily: 'inherit',
    border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff',
    cursor: 'pointer', transition: 'border-color 0.15s',
  },
  triggerOpen: { borderColor: '#c8a147', boxShadow: '0 0 0 2px rgba(200,161,71,0.15)' },
  dropdown: {
    position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 50,
    overflow: 'hidden',
  },
  searchRow: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '8px 10px', borderBottom: '1px solid #f1f5f9',
  },
  searchInput: {
    all: 'unset', flex: 1, fontSize: 13, fontFamily: 'inherit', color: '#0f172a',
  },
  list: {
    maxHeight: 200, overflowY: 'auto', padding: '4px 0',
  },
  item: {
    all: 'unset', display: 'flex', alignItems: 'center', gap: 8, width: '100%',
    boxSizing: 'border-box', padding: '8px 12px', fontSize: 13, fontFamily: 'inherit',
    color: '#334155', cursor: 'pointer', transition: 'background 0.1s',
  },
  itemSelected: {
    background: 'rgba(200,161,71,0.08)', color: '#0f172a', fontWeight: 600,
  },
  empty: {
    padding: '12px 16px', fontSize: 13, color: '#94a3b8', textAlign: 'center',
  },
  addSection: {
    borderTop: '1px solid #f1f5f9', padding: 6,
  },
  addBtn: {
    all: 'unset', display: 'flex', alignItems: 'center', gap: 6, width: '100%',
    boxSizing: 'border-box', padding: '8px 10px', fontSize: 13, fontFamily: 'inherit',
    color: '#c8a147', fontWeight: 600, cursor: 'pointer', borderRadius: 6,
    transition: 'background 0.1s',
  },
  addRow: {
    display: 'flex', alignItems: 'center', gap: 4, padding: '2px',
  },
  addInput: {
    flex: 1, padding: '6px 10px', fontSize: 13, fontFamily: 'inherit',
    border: '1px solid #e2e8f0', borderRadius: 6, outline: 'none',
  },
  addConfirmBtn: {
    all: 'unset', display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 30, height: 30, borderRadius: 6, background: '#c8a147', color: '#fff',
    cursor: 'pointer', flexShrink: 0,
  },
  addCancelBtn: {
    all: 'unset', display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 30, height: 30, borderRadius: 6, border: '1px solid #e2e8f0', color: '#64748b',
    cursor: 'pointer', flexShrink: 0,
  },
};

const SaveButton = ({ selectedId, applying, deviceName, deviceLocation, onApply }) => {
  const missingIdentity = !deviceName.trim() || !deviceLocation.trim();
  const cantSave = !selectedId || applying || missingIdentity;
  return (
    <button
      type="button"
      onClick={onApply}
      disabled={cantSave}
      style={{ ...styles.primaryBtn, opacity: cantSave ? 0.6 : 1 }}
      title={missingIdentity ? 'Screen name and location are required' : undefined}
    >
      {applying ? 'Saving…' : 'Save'}
    </button>
  );
};

const styles = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 20,
  },
  modal: {
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25)',
    width: '100%',
    maxWidth: 720,
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    fontFamily: 'inherit',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '20px 24px 16px',
    borderBottom: '1px solid #e2e8f0',
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: '#0f172a',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 13,
    color: '#64748b',
    lineHeight: 1.5,
  },
  edidLine: {
    fontSize: 12,
    color: '#64748b',
    marginTop: 8,
    padding: '6px 10px',
    background: '#f1f5f9',
    borderRadius: 6,
    display: 'inline-block',
  },
  closeBtn: {
    padding: 6,
    border: '1px solid #e2e8f0',
    borderRadius: 6,
    background: '#fff',
    color: '#64748b',
    cursor: 'pointer',
    display: 'flex',
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: 20,
  },
  identitySection: {
    display: 'flex',
    gap: 12,
    marginBottom: 20,
    paddingBottom: 20,
    borderBottom: '1px solid #e2e8f0',
  },
  identityField: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  identityLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  identityInput: {
    padding: '8px 12px',
    fontSize: 14,
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    outline: 'none',
    fontFamily: 'inherit',
    transition: 'border-color 0.15s',
    appearance: 'none',
    WebkitAppearance: 'none',
  },
  identityInlineBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 34,
    height: 34,
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    background: '#fff',
    color: '#64748b',
    cursor: 'pointer',
    flexShrink: 0,
  },
  loading: {
    padding: '40px 20px',
    textAlign: 'center',
    color: '#94a3b8',
    fontSize: 14,
  },
  error: {
    padding: '10px 14px',
    borderRadius: 6,
    background: '#fef2f2',
    color: '#b91c1c',
    fontSize: 13,
    border: '1px solid #fecaca',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 10,
  },
  card: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '14px 16px',
    border: '2px solid #e2e8f0',
    borderRadius: 10,
    background: '#fff',
    cursor: 'pointer',
    fontFamily: 'inherit',
    textAlign: 'left',
    transition: 'all 120ms ease',
  },
  cardSelected: {
    borderColor: '#c8a147',
    background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
    boxShadow: '0 2px 10px rgba(200, 161, 71, 0.18)',
  },
  addCard: {
    justifyContent: 'center',
    color: '#64748b',
    borderStyle: 'dashed',
    background: '#f8fafc',
  },
  cardName: {
    fontSize: 15,
    fontWeight: 700,
    color: '#0f172a',
  },
  cardMeta: {
    fontSize: 12,
    color: '#475569',
    marginTop: 2,
  },
  cardDesc: {
    fontSize: 11,
    color: '#94a3b8',
    marginTop: 4,
    lineHeight: 1.5,
  },
  matchBadge: {
    fontSize: 10,
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 999,
    background: '#dcfce7',
    color: '#16a34a',
    letterSpacing: 0.3,
    textTransform: 'uppercase',
  },
  userBadge: {
    fontSize: 10,
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 999,
    background: '#ede9fe',
    color: '#7c3aed',
    letterSpacing: 0.3,
    textTransform: 'uppercase',
  },
  selectedCheck: {
    width: 26,
    height: 26,
    borderRadius: '50%',
    background: '#c8a147',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  formRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  label: {
    fontSize: 11,
    fontWeight: 700,
    color: '#475569',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  input: {
    padding: '8px 10px',
    fontSize: 14,
    border: '1px solid #cbd5e1',
    borderRadius: 6,
    fontFamily: 'inherit',
    outline: 'none',
  },
  helpText: {
    fontSize: 11,
    color: '#94a3b8',
    marginTop: 4,
    lineHeight: 1.5,
  },
  linkBtn: {
    background: 'none',
    border: 'none',
    color: '#c8a147',
    fontSize: 13,
    cursor: 'pointer',
    padding: '4px 0',
    textAlign: 'left',
    fontWeight: 600,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
    padding: '16px 24px',
    borderTop: '1px solid #e2e8f0',
    background: '#f8fafc',
  },
  cancelBtn: {
    padding: '9px 16px',
    fontSize: 14,
    fontWeight: 600,
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    background: '#fff',
    color: '#475569',
    cursor: 'pointer',
  },
  primaryBtn: {
    padding: '9px 18px',
    fontSize: 14,
    fontWeight: 700,
    border: 'none',
    borderRadius: 8,
    background: '#c8a147',
    color: '#fff',
    cursor: 'pointer',
  },
};

export default ProfilePickerModal;
